/* eslint-disable react/prop-types */
import { makeStyles } from "@material-ui/core";
import mixpanelTrack from "app/common/MixpanelComponent";
import { setReferenceScenarioId } from "app/summary/duck/slices";
import { nFormatter } from "lib/utils";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { AiOutlineComment } from "react-icons/ai";
import { FaFunnelDollar, FaRegCalendarAlt, FaUser } from "react-icons/fa";
import { GiMoneyStack, GiShoppingCart } from "react-icons/gi";
import { IoIosPricetags } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { kpisTerminology } from "resources/constants";

const styles = makeStyles((theme) => ({
  scenarioSelectorHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "5px 0px",
    boxSizing: "border-box",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
  addScenarioButton: {
    height: "30px",
    padding: "0px",
    width: "30px",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    fontSize: "inherit",
    fontFamily: "inherit",
    "&:hover": {
      backgroundColor: "#D45F59",
    },
    [theme.breakpoints.up(2560)]: { fontSize: "24px" },
    [theme.breakpoints.between(1920, 2560)]: { fontSize: "22px" },
    [theme.breakpoints.between(1440, 1920)]: { fontSize: "20px" },
    [theme.breakpoints.between(1280, 1440)]: { fontSize: "14px" },
    [theme.breakpoints.down(1280)]: { fontSize: "14px" },
  },
  dropdown: {
    position: "absolute",
    display: "inline-block",
    zIndex: 10,
    width: "500px",
    fontSize: "inherit",
    fontFamily: "inherit",
    [theme.breakpoints.down("md")]: {
      right: 50, // Align to the left when screen size is <= 960px
      left: "auto",
    },
  },
  dropdownFromEmptyCard: {
    [theme.breakpoints.up(2560)]: { left: 720, right: "auto" },
    [theme.breakpoints.between(1920, 2560)]: { left: 650, right: "auto" },
    [theme.breakpoints.between(1440, 1920)]: { left: 590, right: "auto" },
    [theme.breakpoints.between(1280, 1440)]: { left: 550, right: "auto" },
    [theme.breakpoints.down(1280)]: {
      right: "auto", // Align to the left when screen size is <= 960px
      left: "auto",
      top: 350,
    },
  },
  menu: {
    padding: "0px 15px 15px 15px",
    backgroundColor: "white",
    border: "1px solid #ddd",
    borderRadius: "8px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    width: "100%",
    maxHeight: "550px",
    overflowY: "auto",
    fontSize: "inherit",
    fontFamily: "inherit",
    [theme.breakpoints.down("md")]: {
      marginLeft: "0px",
      marginTop: "35px",
      marginRight: "35px",
      maxHeight: "300px",
    },
  },
  scenario: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    padding: "15px 5px",
    gap: "5px",
    borderBottom: "1px solid #cfcfcf",
    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.07)",
    overflow: "hidden",
    cursor: "pointer",
    fontSize: "inherit",
    fontFamily: "inherit",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
  },
  scenarioInfo: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    width: "100%",
    flexGrow: "1",
    flexShrink: "0",
    flexBasis: "0",
    gap: "8px",
    overflow: "hidden",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
  infoItem: {
    display: "flex",
    overflow: "hidden",
    gap: "4px",
    alignItems: "center",
    width: "100%",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
  infoItemText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "inherit",
    fontFamily: "inherit",
    // display: "-webkit-box",
    // "-webkit-box-orient": "vertical",
    // "-webkit-line-clamp": 2,
  },
  kpiItem: {
    display: "flex",
    justifyContent: "start",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
  kpiName: {
    flexGrow: "1",
    flexShrink: "0",
    flexBasis: "0",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
  kpiIconData: {
    flexGrow: "2",
    flexShrink: "0",
    flexBasis: "0",
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    gap: "5px",
    fontSize: "inherit",
    fontFamily: "inherit",
  },
  positive: {
    color: "green",
  },
  negative: {
    color: "red",
  },
}));

export default function ScenariosDropdownPromotionsList({
  setSelectedScenario,
}) {
  // Hook Instances
  const dispatch = useDispatch();
  const classes = styles();

  // Hook Global States
  const scenariosSlice = useSelector((state) => state.scenarios);
  const {
    referenceScenario: { scenario_id: globalReferenceScenarioId },
  } = useSelector((state) => state.scenarios);
  const {
    selectedScenarios,
    referenceScenarioId: summaryReferenceScenarioId,
  } = useSelector((state) => state.summary);
  const {
    features: { isUsaTerminology },
  } = useSelector((state) => state.auth.configCatUser);

  // Hook Local States
  // const [isWarningOpen, setIsWarningOpen] = useState(false);
  // const [addScenarioWarningOpen, setAddScenarioWarningOpen] = useState(false);
  const [visibleScenarios, setVisibleScenarios] = React.useState(
    scenariosSlice.totalsByScenarioId
  );
  const [selectedCycle, setSelectedCycle] = useState(
    scenariosSlice.selectedScenario.scenario.cycle_id
  );
  const [referenceChangeTrigger, setReferenceChangeTrigger] = useState(false);

  // Constants
  const { PriceOut, SalesOut, SellOut, Margin } = kpisTerminology(
    isUsaTerminology
  );

  // Functions
  const getTotalIcon = (key) => {
    switch (key) {
      case Margin:
        return <FaFunnelDollar size={20} />;
      case SalesOut:
        return <GiMoneyStack size={22} />;
      case SellOut:
        return <GiShoppingCart size={22} />;
      case PriceOut:
        return <IoIosPricetags size={22} />;
      default:
        return null;
    }
  };

  const applyScenarioFilters = () => {
    const filteredScenarios = _.filter(
      scenariosSlice.totalsByScenarioId,
      (scenarioTotal) => {
        return (
          scenarioTotal.scenario.cycle.id === selectedCycle &&
          !Array.from(selectedScenarios).includes(scenarioTotal.scenario.id)
        );
      }
    );
    return filteredScenarios;
  };

  const onClickScenarioitem = (scenarioId) => {
    const newSelectedScenario = scenariosSlice.totalsByScenarioId[scenarioId];
    setSelectedScenario(newSelectedScenario);
    /* MIXPANEL TRACKS */
    mixpanelTrack("Value destroyers scenario selected");
    /* END OF MIXPANEL TRACKS */
  };

  // Hook Effects
  // Capture reference scenario id change
  useEffect(() => {
    if (globalReferenceScenarioId !== summaryReferenceScenarioId) {
      dispatch(setReferenceScenarioId(globalReferenceScenarioId));
      setReferenceChangeTrigger(true);
    }
  }, [globalReferenceScenarioId]);

  // Reset selected scenarios when reference scenario changes
  useEffect(() => {
    if (referenceChangeTrigger) {
      setReferenceChangeTrigger(false);
    }
  }, [referenceChangeTrigger]);

  // Change selected cycle based on selected scenario
  useEffect(() => {
    setSelectedCycle(scenariosSlice.selectedScenario.scenario.cycle_id);
  }, [scenariosSlice.selectedScenario.scenario.cycle_id]);

  // Filter visible scenarios based on selected cycle and already selected scenarios
  useEffect(() => {
    setVisibleScenarios(applyScenarioFilters());
  }, [selectedCycle, selectedScenarios, scenariosSlice.totalsByScenarioId]);

  return (
    <>
      {visibleScenarios.length > 0 && (
        <div className={classes.menu}>
          {visibleScenarios.map((scenario) => (
            <div
              key={scenario.scenario.name}
              className={classes.scenario}
              role="button"
              tabIndex={0}
              onClick={() => {
                onClickScenarioitem(scenario.scenario.id);
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  onClickScenarioitem(scenario.scenario.id);
                }
              }}
            >
              <div className={classes.scenarioInfo}>
                <strong className={classes.infoItemText}>
                  {scenario.scenario.name}
                </strong>
                <div className={classes.infoItem}>
                  <AiOutlineComment />
                  <div className={classes.infoItemText}>
                    {scenario.scenario.comment || "-"}
                  </div>
                </div>
                <div className={classes.infoItem}>
                  <FaRegCalendarAlt />
                  <div className={classes.infoItemText}>
                    {scenario.scenario.cycle.label}
                  </div>
                </div>
                <div className={classes.infoItem}>
                  <FaUser />
                  <div className={classes.infoItemText}>
                    {scenario.scenario.owner_label}
                  </div>
                </div>
              </div>
              <div className={classes.scenarioInfo}>
                <div className={classes.kpiItem}>
                  <span className={classes.kpiName}>{SellOut}:</span>
                  <span className={classes.kpiIconData}>
                    {getTotalIcon(SellOut)}
                    {`${nFormatter(scenario.units)} Pieces`}
                  </span>
                </div>
                <div className={classes.kpiItem}>
                  <span className={classes.kpiName}>{SalesOut}:</span>
                  <span className={classes.kpiIconData}>
                    {getTotalIcon(SalesOut)}
                    {`$${nFormatter(scenario.sales)}`}
                  </span>
                </div>
                <div className={classes.kpiItem}>
                  <span className={classes.kpiName}>{Margin}:</span>
                  <span className={classes.kpiIconData}>
                    {getTotalIcon(Margin)}
                    {`$${nFormatter(scenario.margin)}`}
                  </span>
                </div>
                <div className={classes.kpiItem}>
                  <span className={classes.kpiName}>{PriceOut}:</span>
                  <span className={classes.kpiIconData}>
                    {getTotalIcon(PriceOut)}
                    {`$${nFormatter(scenario.weighted_avg_price_units)}`}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
